// api.ts — typed client for the DAW-Streamer server REST API.
//
// Contract: docs/PROTOCOL.md §3 (Server HTTP/JSON API). All paths are under
// the base path `/api`. The base origin is configurable via the Vite env var
// VITE_API_BASE; when empty (the default, and the production case) the app
// calls the same origin it was served from — the Rust server hosts both the
// static app and the API, so relative URLs "just work".

/** Base URL for the server. Empty string => same-origin (production default). */
const API_BASE: string = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

/** Build an absolute (or same-origin-relative) URL for an /api path. */
function apiUrl(path: string): string {
  if (!path.startsWith("/")) path = "/" + path;
  return `${API_BASE}${path}`;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Hard deadline for every API call (ms). REVIEW 2.3: nothing here had a
 *  timeout, while four pollers (health 5 s, host roster 5 s, waiting 4 s,
 *  approval 2 s) fire on the clock regardless of whether the previous request
 *  ever came back. On a stalled network the open requests stacked up — and the
 *  ones that would have DETECTED the stall queued behind them. A request that
 *  has not answered within this window is worthless anyway: the next tick is
 *  already due. */
const REQUEST_TIMEOUT_MS = 8000;

/** fetch with a hard deadline. Every call in this module goes through it, so
 *  no request can outlive its own poll interval (REVIEW 2.3). */
async function fetchWithDeadline(url: string, init: RequestInit = {}): Promise<Response> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  headers.set("Accept", "application/json");

  let res: Response;
  try {
    res = await fetchWithDeadline(apiUrl(path), { ...init, headers, body });
  } catch (e) {
    // Network / DNS / TLS failure — surface a uniform shape to the UI.
    const err = e as Error;
    const msg =
      err.name === "AbortError"
        ? `No answer within ${REQUEST_TIMEOUT_MS / 1000} s`
        : `Network error: ${err.message}`;
    throw new ApiError(0, "network", msg);
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
  }

  if (!res.ok) {
    const obj = (parsed ?? {}) as { code?: string; message?: string; error?: string };
    throw new ApiError(
      res.status,
      obj.code ?? "http_" + res.status,
      obj.message ?? obj.error ?? `HTTP ${res.status}`,
    );
  }
  return parsed as T;
}

// ---------------------------------------------------------------------------
// Shapes (mirror docs/PROTOCOL.md §3 exactly)
// ---------------------------------------------------------------------------

/** Per-session branding applied at runtime (logo slot + colors + look). */
export interface Branding {
  /** Display title shown in the header (falls back to session name). */
  title?: string;
  /** Absolute or app-relative URL to a logo image for the logo slot. */
  logoUrl?: string;
  /** Primary accent color, any CSS color. Maps to --brand-accent. */
  accent?: string;
  /** Page background color. Maps to --brand-bg. */
  bg?: string;
  /** Foreground / text color. Maps to --brand-fg. */
  fg?: string;
  /** Optional surface/panel color. Maps to --brand-surface. */
  surface?: string;
  /** Optional muted text color. Maps to --brand-muted. */
  muted?: string;
}

/**
 * Comment moderation state for the session (docs §7.3).
 *  - `enabled`:     when false the listener comment input is hidden/disabled.
 *  - `privateMode`: when true GET returns (and the room only delivers) the
 *                   caller's own comments.
 */
export interface CommentConfig {
  enabled: boolean;
  privateMode: boolean;
}

/** Public-safe session descriptor — GET /api/sessions/:id. */
export interface SessionInfo {
  sessionId: string;
  name: string;
  branding: Branding;
  requirePassword: boolean;
  requireApproval: boolean;
  /** Whether the streamer is currently live (ingest connected). */
  live: boolean;
  /** Comment moderation config (docs §7.3). Absent ⇒ enabled, non-private. */
  commentConfig?: CommentConfig;
  /** §9: whether the call join requires a password (join is otherwise open). */
  callRequiresPassword?: boolean;
}

/**
 * Result of join / poll. Discriminated on `status`.
 * The `ok` branch carries the server-assigned `color` for this clientId
 * (docs §7.1) — a hex string from the fixed palette, stable for the session.
 */
export type JoinResult =
  | {
      status: "ok";
      livekitUrl: string;
      token: string;
      room: string;
      color?: string;
      /** §16: joined as the session owner (streamer JWT recognised). */
      isHost?: boolean;
      /** §16: effective name (the server may override the host's). */
      displayName?: string;
    }
  | { status: "pending"; waitId: string }
  /** §24: registered and visible to the host, but not in the session yet —
   *  either it has not been opened or the host admits people one by one. */
  | {
      status: "waiting";
      name: string;
      others: string[];
      admitOneByOne: boolean;
    }
  | { status: "denied" };

export interface Comment {
  id: string;
  projectSec: number;
  text: string;
  author: string;
  /** Server wall-clock epoch milliseconds. */
  ts: number;
  /** Owner clientId (authoritative for edit/delete rights, docs §7.2). */
  clientId?: string;
  /** Owner color (hex from the palette, docs §7.1/§7.2). */
  color?: string;
  /** True once the comment has been edited (docs §7.2). */
  edited?: boolean;
  resolved?: boolean;
  resolutionVersion?: number;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** GET /api/sessions/:id — public-safe session info (no auth). */
/** Share-link key from the page URL (?k=...). The server requires it on the
 *  session GET and the join — the friendly session id alone must not admit
 *  anyone (ids like "mix" are guessable). */
export function linkKeyFromUrl(): string {
  try {
    return new URL(window.location.href).searchParams.get("k")?.trim() ?? "";
  } catch {
    return "";
  }
}

export function getSession(sessionId: string): Promise<SessionInfo> {
  const k = linkKeyFromUrl();
  const q = k ? `?k=${encodeURIComponent(k)}` : "";
  return request<SessionInfo>(`/api/sessions/${encodeURIComponent(sessionId)}${q}`);
}

/**
 * POST /api/sessions/:id/join — attempt to join.
 * Returns ok (token + assigned color), pending (knock + waitId), or denied.
 * `clientId` (docs §7.1) identifies this listener across reloads of the same
 * session so the server can assign a stable palette color and authorize
 * edit/delete on the caller's own comments.
 */
export function joinSession(
  sessionId: string,
  displayName: string,
  clientId: string,
  password?: string,
): Promise<JoinResult> {
  const json: { displayName: string; clientId: string; password?: string; k?: string; hostName?: string } = {
    displayName,
    clientId,
  };
  if (password) json.password = password;
  const k = linkKeyFromUrl();
  if (k) json.k = k;
  // §16: if this browser is logged in as the streamer (host dashboard, same
  // origin), identify — the owner joins their own session as HOST.
  const hostToken = localStorage.getItem("daws:host:token");
  if (hostToken) json.hostName = localStorage.getItem("daws:host:name")?.trim() || "Julius";
  const headers: Record<string, string> = hostToken
    ? { Authorization: `Bearer ${hostToken}` }
    : {};
  return request<JoinResult>(
    `/api/sessions/${encodeURIComponent(sessionId)}/join`,
    { method: "POST", json, headers },
  );
}

/**
 * POST /api/sessions/:id/call/join — join the VIDEO CALL (docs §9.1). Gated on
 * the session's separate call password; mints a microphone-only call token
 * (camera is host-granted later). 403 when the call is closed or the password
 * is wrong. Returns the same ok-shape as the listener join.
 */
export function callJoin(
  sessionId: string,
  displayName: string,
  password?: string,
): Promise<Extract<JoinResult, { status: "ok" }>> {
  const json: { displayName: string; password?: string } = { displayName };
  if (password) json.password = password;
  return request<Extract<JoinResult, { status: "ok" }>>(
    `/api/sessions/${encodeURIComponent(sessionId)}/call/join`,
    { method: "POST", json },
  );
}

/**
 * POST /api/sessions/:id/call/participants/:identity/grant — host live-updates a
 * call participant's publish sources (§9, streamer JWT). `sources` is the
 * COMPLETE new set; revoking video = ["microphone"] again.
 */
export function grantCallSources(
  sessionId: string,
  identity: string,
  sources: string[],
  hostToken: string,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/call/participants/${encodeURIComponent(identity)}/grant`,
    {
      method: "POST",
      json: { sources },
      headers: { Authorization: `Bearer ${hostToken}` },
    },
  );
}

/**
 * POST /api/sessions/:id/call/host-token — the HOST's own call token (§9): all
 * sources + roomAdmin. Authorized by the streamer dashboard JWT (session owner).
 */
export function callHostToken(
  sessionId: string,
  hostToken: string,
): Promise<Extract<JoinResult, { status: "ok" }>> {
  return request<Extract<JoinResult, { status: "ok" }>>(
    `/api/sessions/${encodeURIComponent(sessionId)}/call/host-token`,
    { method: "POST", headers: { Authorization: `Bearer ${hostToken}` } },
  );
}

/** POST …/call/participants/:identity/mute — force-mute a published track (§9, host). */
export function muteCallParticipant(
  sessionId: string,
  identity: string,
  trackSid: string,
  hostToken: string,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/call/participants/${encodeURIComponent(identity)}/mute`,
    { method: "POST", json: { trackSid }, headers: { Authorization: `Bearer ${hostToken}` } },
  );
}

/** DELETE …/call/participants/:identity — kick a call participant (§9, host). */
export function kickCallParticipant(
  sessionId: string,
  identity: string,
  hostToken: string,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/call/participants/${encodeURIComponent(identity)}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${hostToken}` } },
  );
}

/** GET /api/sessions/:id/join/:waitId — poll guest approval. */
export function pollJoin(sessionId: string, waitId: string): Promise<JoinResult> {
  return request<JoinResult>(
    `/api/sessions/${encodeURIComponent(sessionId)}/join/${encodeURIComponent(waitId)}`,
  );
}

/**
 * GET /api/sessions/:id/comments — fetch the comment list.
 *
 * Per PROTOCOL §7.3, when the session is in privateMode the server returns only
 * the caller's own comments, identified by `clientId`. We therefore must thread
 * the listener's `clientId` through as the `?clientId=` query param (matching the
 * server's `CommentsQuery` deserializer); without it a private-mode session
 * returns an empty list to non-streamer callers.
 */
export async function getComments(
  sessionId: string,
  opts: { token?: string; clientId?: string } = {},
): Promise<Comment[]> {
  const init: RequestInit = {};
  if (opts.token) init.headers = { Authorization: `Bearer ${opts.token}` };
  let path = `/api/sessions/${encodeURIComponent(sessionId)}/comments`;
  if (opts.clientId) path += `?clientId=${encodeURIComponent(opts.clientId)}`;
  const r = await request<{ comments: Comment[] }>(path, init);
  return r.comments ?? [];
}

/**
 * POST /api/sessions/:id/comments — submit a comment (docs §7.3).
 * The caller attaches the FROZEN projectSec (the position it had anchored on,
 * docs §4/§7) so the comment lands on the project position the listener heard,
 * plus its `clientId` so the server records ownership and color.
 * Requires a valid listener/guest token (the LiveKit token from /join).
 * Rejected 403 when comments are disabled or this clientId is denied.
 */
export async function postComment(
  sessionId: string,
  body: { text: string; projectSec: number; author: string; clientId: string },
  token?: string,
): Promise<{ id: string }> {
  const init: RequestInit & { json?: unknown } = { method: "POST", json: body };
  if (token) init.headers = { Authorization: `Bearer ${token}` };
  return request<{ id: string }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/comments`,
    init,
  );
}

/**
 * POST /api/sessions/:id/health — report this listener's connection-health level
 * (0 good / 1 marginal / 2 poor) from its WebRTC jitter-buffer stats (docs §7.9).
 * Fire-and-forget telemetry that drives the plugin's latency-adequacy indicator.
 */
export async function reportHealth(
  sessionId: string,
  clientId: string,
  level: number,
  token: string,
): Promise<
  { canPause: boolean; canComment: boolean | null; canCall: boolean; streamPaused?: boolean } | null | "forbidden"
> {
  try {
    const r = await fetchWithDeadline(apiUrl(`/api/sessions/${encodeURIComponent(sessionId)}/health`), {
      method: "POST",
      headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ clientId, level }),
    });
    // 401/403 = the server does not KNOW us (roster wiped by a restart, token
    // stale). Distinct from a flaky poll: the caller re-joins on this.
    if (r.status === 401 || r.status === 403) return "forbidden";
    if (!r.ok) return null;
    // The response piggybacks this listener's PERSONAL grants (§12 remote pause,
    // §7.3 comment permission) — the session GET only knows the global config.
    const body = (await r.json().catch(() => null)) as
      | { canPause?: boolean; canComment?: boolean; canCall?: boolean; streamPaused?: boolean }
      | null;
    if (!body) return null;
    return {
      canPause: body.canPause === true,
      canComment: typeof body.canComment === "boolean" ? body.canComment : null,
      canCall: body.canCall === true,
      streamPaused: body.streamPaused,
    };
  } catch {
    // Failure returns NULL — never an all-false grant set. One flaky poll used
    // to silently DEMOTE the listener (mic force-released, then a fresh
    // promotion toast + permission dance when the next poll succeeded).
    return null;
  }
}

/** POST /api/sessions/:id/pause — §12 remote pause (host-granted listeners only):
 *  pauses the host's DAW (REAPER Transport: Pause). 403 when not granted. */
/** §13 HOST: jump REAPER's edit cursor to a comment's position. */
export async function requestSeek(
  sessionId: string,
  projectSec: number,
  hostToken: string,
): Promise<void> {
  await request(`/api/sessions/${encodeURIComponent(sessionId)}/seek`, {
    method: "POST",
    headers: { Authorization: `Bearer ${hostToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ projectSec }),
  });
}

export async function requestPause(sessionId: string, token: string): Promise<void> {
  const r = await fetchWithDeadline(apiUrl(`/api/sessions/${encodeURIComponent(sessionId)}/pause`), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(r.status, "pause", body?.error ?? `pause failed (${r.status})`);
  }
}

/**
 * PATCH /api/sessions/:id/comments/:cid — edit a comment's text (docs §7.3).
 * Authorized by owner `clientId` (in the body) OR a streamer JWT (host
 * moderation, `Authorization: Bearer`). Sets `edited:true` server-side.
 */
export async function editComment(
  sessionId: string,
  commentId: string,
  text: string,
  auth: { clientId?: string; listenerToken?: string; hostToken?: string },
): Promise<{ ok: true }> {
  const json: { text: string; clientId?: string } = { text };
  if (auth.clientId) json.clientId = auth.clientId;
  const init: RequestInit & { json?: unknown } = { method: "PATCH", json };
  // Non-host callers authenticate with their listener token (proof of session
  // membership); the host overrides with the streamer JWT. The server binds the
  // clientId owner-match to whichever token is presented.
  const bearer = auth.hostToken ?? auth.listenerToken;
  if (bearer) init.headers = { Authorization: `Bearer ${bearer}` };
  return request<{ ok: true }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/comments/${encodeURIComponent(commentId)}`,
    init,
  );
}

/**
 * DELETE /api/sessions/:id/comments/:cid — remove a comment (docs §7.3).
 * Authorized by owner `clientId` (header `X-Client-Id`) + the caller's listener
 * token, OR a streamer JWT (host moderation). Both go in `Authorization: Bearer`.
 */
export async function deleteComment(
  sessionId: string,
  commentId: string,
  auth: { clientId?: string; listenerToken?: string; hostToken?: string },
): Promise<{ ok: true }> {
  const headers = new Headers();
  if (auth.clientId) headers.set("X-Client-Id", auth.clientId);
  const bearer = auth.hostToken ?? auth.listenerToken;
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);
  return request<{ ok: true }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE", headers },
  );
}

/**
 * POST /api/auth/login — streamer login for HOST mode (docs §3 / §7.3).
 * Returns a streamer JWT used to moderate (edit/delete) ALL comments via
 * `Authorization: Bearer`.
 */
export function login(
  username: string,
  password: string,
): Promise<{ token: string; expiresAt: number }> {
  return request<{ token: string; expiresAt: number }>("/api/auth/login", {
    method: "POST",
    json: { username, password },
  });
}

// ---------------------------------------------------------------------------
// Host dashboard (streamer-only; Authorization: Bearer <login token>)
// ---------------------------------------------------------------------------

/** One session-history entry (host dashboard + plugin "revive"). */
export interface HostSession {
  id: string;
  name: string;
  owner: string;
  createdMs: number;
  lastLiveMs: number;
  /** Streamer currently live (ingest connected) for this id. */
  live: boolean;
  /** Share-link key — the dashboard's Open button must carry it. */
  linkKey?: string;
}

/** GET /api/host/sessions — recent sessions (most-recent first) for the dashboard. */
/** DELETE /api/host/sessions/:id — remove an old session (dashboard cleanup).
 *  409 while the session is live (stop streaming first). */
/** §16: did the PLUGIN open this page ("&host=1")? Then the opener means to
 *  be host, and a plain-listener join is a failure worth showing, not a
 *  silent fallback. */
export function hostIntentFromUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).get("host") === "1";
  } catch {
    return false;
  }
}

/** §18 host page: soft-kick a participant — by LiveKit identity (mixer rows)
 *  or by the stable roster clientId (the listener list). */
export async function hostKickParticipant(
  sessionId: string,
  target: { identity?: string; clientId?: string },
  token: string,
): Promise<void> {
  const r = await fetchWithDeadline(apiUrl(`/api/sessions/${encodeURIComponent(sessionId)}/kick`), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(target),
  });
  if (!r.ok) throw new Error(`kick failed (${r.status})`);
}

/** One roster row as the host page sees it (wire shape of the plugin roster). */
export interface HostRosterMember {
  identity?: string;
  clientId: string;
  name: string;
  color: string;
  canCall?: boolean;
  canComment?: boolean;
  canChat?: boolean;
  canPause?: boolean;
  waiting?: boolean;
  kicked?: boolean;
  present: boolean;
}

/** §18: the live roster for the host page. Owner-gated. */
export async function getHostRoster(
  sessionId: string,
  token: string,
): Promise<HostRosterMember[]> {
  const r = await fetchWithDeadline(apiUrl(`/api/sessions/${encodeURIComponent(sessionId)}/roster`), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`roster failed (${r.status})`);
  const j = (await r.json()) as { members?: HostRosterMember[] };
  return j.members ?? [];
}

export async function deleteHostSession(id: string, token: string): Promise<void> {
  const r = await fetchWithDeadline(apiUrl(`/api/host/sessions/${encodeURIComponent(id)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(r.status, "delete", body?.error ?? `delete failed (${r.status})`);
  }
}

export async function getHostSessions(token: string): Promise<HostSession[]> {
  const r = await request<{ sessions: HostSession[] }>("/api/host/sessions", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return r.sessions ?? [];
}

/** §10 device registration as seen by the host dashboard (never carries a token). */
export interface HostDevice {
  deviceId: string;
  name: string;
  userCode: string;
  status: "pending" | "approved" | "revoked";
  owner: string;
  createdMs: number;
  approvedMs: number;
}

/** GET /api/host/devices — plugin devices (pending to approve, approved to revoke). */
export async function getHostDevices(token: string): Promise<HostDevice[]> {
  const r = await request<{ devices: HostDevice[] }>("/api/host/devices", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return r.devices ?? [];
}

/** POST /api/host/devices/:id/approve — authorize a pending device (§10). */
export function approveDevice(deviceId: string, token: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/host/devices/${encodeURIComponent(deviceId)}/approve`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` } },
  );
}

/** POST /api/host/devices/:id/revoke — withdraw a device's access (§10). */
export function revokeDevice(deviceId: string, token: string): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/host/devices/${encodeURIComponent(deviceId)}/revoke`,
    { method: "POST", headers: { Authorization: `Bearer ${token}` } },
  );
}

export interface HostSessionState {
  passwordProtected: boolean;
  members: HostRosterMember[];
  open: boolean;
  admitOneByOne: boolean;
  allowCall: boolean;
  allowPause: boolean;
  allowComment: boolean;
  sourceConnected: boolean;
  sourcePaused: boolean;
  privateMonitoring: boolean;
  communicationOpen: boolean;
  latencyMs: number;
  waiting: number;
}
export function getHostSessionState(id: string, token: string): Promise<HostSessionState> {
  return request(`/api/sessions/${encodeURIComponent(id)}/roster`, { headers: { Authorization: `Bearer ${token}` } });
}
export function updateHostSession(id: string, token: string, changes: Partial<Pick<HostSessionState, "open" | "admitOneByOne" | "allowCall" | "allowPause" | "allowComment">>): Promise<void> {
  return request(`/api/sessions/${encodeURIComponent(id)}/session-perm`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, json: changes });
}
export function setPrivateMonitoring(id: string, token: string, enabled: boolean): Promise<void> {
  return request(`/api/sessions/${encodeURIComponent(id)}/music`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, json: { privateMonitoring: enabled } });
}
export function updateParticipant(id: string, token: string, clientId: string, changes: { canCall?: boolean; canChat?: boolean; canComment?: boolean; canPause?: boolean; admit?: boolean }): Promise<void> {
  return request(`/api/sessions/${encodeURIComponent(id)}/perm`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, json: { clientId, ...changes } });
}
export function createHostSession(id: string, token: string): Promise<{ sessionId: string; linkKey: string }> {
  return request("/api/sessions", { method: "POST", headers: { Authorization: `Bearer ${token}` }, json: { id } });
}
export function activateHostSession(id: string, token: string): Promise<{ sessionId: string; linkKey: string }> {
  return request(`/api/host/sessions/${encodeURIComponent(id)}/activate`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
}

export function resolveComment(sessionId: string, commentId: string, resolved: boolean, token: string): Promise<Comment> {
  return request(`/api/sessions/${encodeURIComponent(sessionId)}/comments/${encodeURIComponent(commentId)}/resolve`, {
    method: "POST", headers: { Authorization: `Bearer ${token}` }, json: { resolved },
  });
}

export function updateCommunication(id: string, token: string, open?: boolean): Promise<{open: boolean}> {
  return request(`/api/sessions/${encodeURIComponent(id)}/communication`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, json: open === undefined ? {} : { open } });
}
export interface ChatSnapshot { messages: Comment[]; open: boolean; canWrite: boolean; canComment?: boolean }
export function getChat(id: string, token: string): Promise<ChatSnapshot> {
  return request(`/api/sessions/${encodeURIComponent(id)}/chat`, { headers: { Authorization: `Bearer ${token}` } });
}
export function sendChat(id: string, token: string, text: string): Promise<Comment> {
  return request(`/api/sessions/${encodeURIComponent(id)}/chat`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, json: { text } });
}

/** Streamed host presence survives background-tab timer throttling. */
export function keepCommunicationPresence(id: string, token: string): () => void {
  const controller = new AbortController();
  void (async () => {
    while (!controller.signal.aborted) {
      try {
        const response = await fetch(apiUrl(`/api/sessions/${encodeURIComponent(id)}/communication`), {
          headers: {Authorization: `Bearer ${token}`}, signal: controller.signal,
        });
        if (response.status === 401 || response.status === 403) return;
        if (!response.ok || !response.body) throw new Error("Presence unavailable");
        const reader = response.body.getReader();
        try { while (!(await reader.read()).done) { /* Server owns the lease clock. */ } }
        finally { reader.releaseLock(); }
      } catch { if (controller.signal.aborted) return; }
      await new Promise<void>(resolve => {
        const finish = () => { window.clearTimeout(timer); controller.signal.removeEventListener("abort",finish); resolve(); };
        const timer = window.setTimeout(finish,2000);
        controller.signal.addEventListener("abort",finish,{once:true});
      });
    }
  })();
  return () => controller.abort();
}

export function setGuestPassword(id: string, token: string, password: string): Promise<void> {
  return request(`/api/sessions/${encodeURIComponent(id)}/guest-password`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, json: { password } });
}
